import React, { useState, useRef, useEffect } from "react"
import { device } from "../../utils/breakpoints"

import styled from "styled-components"
import EmblaCarousel from "embla-carousel"
import { ResponsiveGatsbyImage } from "../ResponsiveGatsbyImage"

const Wrapper = styled.div`
  margin: 30px 0;

  @media ${device.desktop} {
    margin: 60px 0;
  }

  figcaption {
    margin-top: 16px;
  }
`
const CarouselWrapper = styled.div`
  position: relative;

  :hover {
    button.arrow {
      opacity: 1;
    }
  }
`

const CarouselViewport = styled.div`
  overflow: hidden;
  width: 100%;
`

const CarouselContainer = styled.div`
  display: flex;
`

const Slide = styled.div`
  position: relative;
  min-width: 100%;
  box-sizing: border-box;
  padding-top: 65%;

  img {
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    position: absolute;
    display: block;
    margin: 0;
    object-fit: cover !important;
  }
`

const Arrow = styled.button`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  display: flex;
  align-items: center;

  background: white;
  border: none;
  height: 48px;
  width: 48px;
  cursor: pointer;
  border-radius: 5px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  opacity: 0;
  transition: 150ms;

  @media (hover: none) and (pointer: coarse) {
    display: none;
  }

  svg {
    width: 16px;
    height: 16px;
    margin-top: 3px;
  }

  :hover {
    svg {
      path {
        fill: #c8295f;
      }
    }
  }

  ${props => (props.left ? "left: -24px;" : "right: -24px")};
`

const Dots = styled.div`
  display: flex;
  margin-top: 15px;
  margin-left: -3px;
`

const Dot = styled.button`
  display: block;
  width: 6px;
  height: 6px;
  flex-grow: 0;
  margin: 0 3px;
  padding: 0;
  border-radius: 100%;
  border: none;
  background: ${props => (props.active ? "#24A49E !important" : "#ccc")};
  cursor: pointer;
  transition: 120ms;

  :hover {
    background: #bbb;
  }
`

const ImageGallery = ({ images, caption }) => {
  const viewportRef = useRef(null)
  const emblaRef = useRef(null)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [scrollSnaps, setScrollSnaps] = useState([])
  const firstImage = images?.[0]
  const imageRatio = Number(firstImage?.width) / Number(firstImage?.height)
  const initialRatio =
    Number.isFinite(imageRatio) && imageRatio > 0 ? imageRatio : 100 / 65
  const [aspectRatio, setAspectRatio] = useState(initialRatio)

  useEffect(() => {
    setAspectRatio(initialRatio)
    if (!firstImage?.src) return

    const image = new Image()
    let active = true
    image.onload = () => {
      const ratio = image.naturalWidth / image.naturalHeight
      if (active && Number.isFinite(ratio) && ratio > 0) setAspectRatio(ratio)
    }
    image.src = firstImage.src

    return () => {
      active = false
      image.onload = null
    }
  }, [firstImage?.src, initialRatio])

  // BASIC CAROUSEL FUNC
  useEffect(() => {
    if (!viewportRef.current) return

    emblaRef.current = EmblaCarousel(viewportRef.current, { loop: true })

    const embla = emblaRef.current
    setScrollSnaps(embla.scrollSnapList())
    setSelectedIndex(embla.selectedScrollSnap())

    const onSelect = () => {
      setSelectedIndex(embla.selectedScrollSnap())
    }

    embla.on("select", onSelect)
    embla.on("reInit", onSelect)

    return () => embla.destroy()
  }, [images?.length])

  const scrollTo = index => {
    if (emblaRef.current) emblaRef.current.scrollTo(index)
  }

  const scrollPrev = () => {
    if (emblaRef.current) emblaRef.current.scrollPrev()
  }

  const scrollNext = () => {
    if (emblaRef.current) emblaRef.current.scrollNext()
  }

  return (
    <Wrapper>
      <CarouselWrapper>
        <Arrow
          left
          onClick={scrollPrev}
          className="arrow"
          aria-label="Previous image"
        >
          <svg viewBox="0 0 10 16" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M.921 8c0-.276.106-.552.316-.763L8.474 0 10 1.526 3.526 8 10 14.474 8.474 16 1.237 8.763A1.076 1.076 0 0 1 .921 8z"
              fill="#E4316E"
            />
          </svg>
        </Arrow>
        <Arrow onClick={scrollNext} className="arrow" aria-label="Next image">
          <svg viewBox="0 0 10 16" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M10 8c0-.276-.106-.552-.316-.763L2.447 0 .92 1.526 7.395 8 .921 14.474 2.447 16l7.237-7.237c.21-.21.316-.487.316-.763z"
              fill="#E4316E"
            />
          </svg>
        </Arrow>
        <CarouselViewport ref={viewportRef}>
          <CarouselContainer>
            {images?.map(({ src, srcSet, alt, sizes }, index) => (
              <Slide
                key={index}
                style={{ paddingTop: `${(1 / aspectRatio) * 100}%` }}
              >
                <ResponsiveGatsbyImage
                  src={src}
                  srcSet={srcSet}
                  alt={alt || ""}
                  sizes={sizes}
                  aspectRatio={aspectRatio}
                />
              </Slide>
            ))}
          </CarouselContainer>
        </CarouselViewport>
      </CarouselWrapper>
      {caption && <figcaption>{caption}</figcaption>}
      <Dots>
        {scrollSnaps.map((_, index) => (
          <Dot
            key={index}
            aria-label={`Go to image ${index + 1}`}
            active={index === selectedIndex}
            onClick={() => scrollTo(index)}
          />
        ))}
      </Dots>
    </Wrapper>
  )
}

export default ImageGallery
